// src/redux/slices/bannerSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_ROUTES from '../../config/apiRoutes';

// Fetch all banners
export const fetchBanners = createAsyncThunk(
  'banners/fetchBanners',
  async (filterObj = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    const requestBody = {
      limit: filterObj.limit ?? '',
      currentPage: filterObj.currentPage ?? '',
      byKeyword: filterObj.byKeyword ?? "",
    };
    try {
      const response = await axios.post(
        API_ROUTES.BANNERS.GET_ALL,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Create a new banner
export const createBanner = createAsyncThunk(
  'banners/createBanner',
  async (bannerData, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.BANNERS.CREATE,
        bannerData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create banner'
      );
    }
  }
);

// Update banner — PATCH /api/v2/banners/admins/:id with JSON body
// { image?, isActive, sequenceNo }. `image` is a base64 string or URL.
export const updateBanner = createAsyncThunk(
  'banners/updateBanner',
  async ({ id, bannerData }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.patch(
        API_ROUTES.BANNERS.UPDATE(id),
        bannerData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Accept either { data: <banner> } or just <banner>.
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update banner'
      );
    }
  }
);

// Delete banner
export const deleteBanner = createAsyncThunk(
  'banners/deleteBanner',
  async (id, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.post(
        API_ROUTES.BANNERS.DELETE(id),
        {}, // Empty body for POST request
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete banner'
      );
    }
  }
);

const bannerSlice = createSlice({
  name: 'banners',
  initialState: {
    banners: [],
    loading: false,
    error: null,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch banners
    builder
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload;
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create banner
    builder
      .addCase(createBanner.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createBanner.fulfilled, (state, action) => {
        state.isCreating = false;
        state.banners.unshift(action.payload);
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      });

    // Update banner
    builder
      .addCase(updateBanner.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.banners.findIndex(
          (banner) => banner._id === action.payload._id
        );
        if (index !== -1) {
          state.banners[index] = action.payload;
        }
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      });

    // Delete banner
    builder
      .addCase(deleteBanner.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.banners = state.banners.filter(
          (banner) => banner._id !== action.payload
        );
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = bannerSlice.actions;
export default bannerSlice.reducer;